import { ErrorPage } from "@/pages/ErrorPage"
import { isQuestionnaireNotFound } from "@/services/api/questionnaireQueryPolicy"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router"

import { useRegisterAgentContext } from "@/features/agent-chat/context/AgentPageContext"

import { toast } from "@/components/common/Toast"
import { IconCheck, IconNotes } from "@tabler/icons-react"

import { AnimatedSkeletonList } from "@/components/common/AnimatedSkeletonList/AnimatedSkeletonList"
import { BreadcrumbsNavigation } from "@/components/common/BreadcrumbsNavigation/BreadcrumbsNavigation"
import { Button } from "@/components/common/Button/Button"
import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import {
  HighlightBoxType,
  LayoutType,
  PdfIntelligenceViewer,
} from "@/components/common/PdfViewer/PdfIntelligenceViewer"
import {
  SplitView,
  SplitViewSection,
} from "@/components/common/SplitView/SplitView"
import { Typography } from "@/components/common/Typography/Typography"
import { AnsweringContextDialog } from "@/components/feature/Modals/AnsweringContextDialog/AnsweringContextDialog"
import { ContinueLaterModal } from "@/components/feature/Modals/ContinueLaterModal/ContinueLaterModal"
import { DeleteQuestionnaireModal } from "@/components/feature/Modals/DeleteQuestionnaireModal/DeleteQuestionnaireModal"
import {
  readCompanyProfilePreference,
  SelectDocumentsDialog,
} from "@/components/feature/Modals/SelectDocumentsDialog/SelectDocumentsDialog"
import { Layout } from "@/components/layout"
import { PERMISSIONS } from "@/constants/permissions"
import { useAgentChatContext } from "@/contexts/AgentChatContext"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs/useBreadcrumbs"
import { usePermissionGuard } from "@/hooks/usePermissionGuard"
import { useQuestionnaireAutofill } from "@/hooks/useQuestionnaireAutofill/useQuestionnaireAutofill"
import { useQuestionnaireStatusNavigation } from "@/hooks/useQuestionnaireStatusNavigation/useQuestionnaireStatusNavigation"
import {
  CATALOG_LANGUAGES,
  getCatalog,
  loadCatalogs,
} from "@/hooks/useTranslation/catalogs"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { mapApiCitationsToCitations } from "@/services/adapters/questionnaireAdapter/questionnaireAdapter"
import {
  useAddManualField,
  useDeleteResponsePair,
  useGetOriginalFile,
  useGetQuestionnaireAnsweringContext,
  useGetQuestionnaireData,
  useGetQuestionnaireFilterConfig,
  useUpdateQuestionnaireStatus,
} from "@/services/api/questionnaireService"
import {
  Questionnaire,
  QuestionnaireResponsePair,
  QuestionnaireStatus,
} from "@/types/questionnaire/models"
import { isLimitError } from "@/utils/limitErrors"
import { getPdfAnswerCounts } from "@/utils/questionnaire/pdfAnswerCounts"

import { Button as ShadCnButton } from "@/components/ui/button"
import { PDF_ANSWER_BOX_DEFAULT_FONT_SIZE } from "@/constants/UiConstants"
import { toastApiError } from "@/features/material-compliance/api/apiError"
import { useHighlightBoxes } from "./hooks/useHighlightBoxes"
import { usePlacementUpdater } from "./hooks/usePlacementUpdater"
import { useQuestionnaireHandlers } from "./hooks/useQuestionnaireHandlers"
import { useQuestionSelection } from "./hooks/useQuestionSelection"
import { useSignatureImageUrls } from "./hooks/useSignatureImageUrls"
import { useSignaturePlacements } from "./hooks/useSignaturePlacements"
import { PdfQASection } from "./PdfQASection"
import {
  createOptionBoxId,
  createSignatureBoxId,
  getSignaturePlacementIdFromBoxId,
  isSignatureBoxId,
} from "./utils/boxIdUtils"
import { clamp } from "./utils/placementUtils"

type TranslateFn = (
  key: TranslationKeys,
  dynamicValues?: Record<string, string | number>,
) => string

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

type CustomBoxKind = "answer" | "checkbox"

// getNextCustomBoxLabel matches labels in every language, so it needs every
// catalog, not only the UI language's. Loaded on the first add rather than on
// page load; a failure only narrows the matching, it must not block the add.
const loadLabelCatalogs = () =>
  loadCatalogs(CATALOG_LANGUAGES).catch(() => undefined)

const getNextCustomBoxLabel = (
  translate: TranslateFn,
  questionnaire: Questionnaire,
  kind: CustomBoxKind,
) => {
  const labelKey =
    kind === "checkbox"
      ? "QUESTIONNAIRE_PDF/CUSTOM_CHECK_BOX_LABEL"
      : "QUESTIONNAIRE_PDF/CUSTOM_ANSWER_BOX_LABEL"
  const prefix = translate(labelKey)
  const targetType = kind === "checkbox" ? "boolean" : "free-text"
  // Match the prefix in every supported language, not just the current one,
  // so switching the UI language never resets the counter and produces
  // duplicate "… 1" labels for boxes created under a different locale.
  // Callers load every catalog first (see loadLabelCatalogs).
  const knownPrefixes = Array.from(
    new Set([
      ...CATALOG_LANGUAGES.map((language) => getCatalog(language)?.[labelKey]),
      prefix,
    ]),
  ).filter((known): known is string => Boolean(known))
  const labelRegex = new RegExp(
    `\\b(?:${knownPrefixes.map(escapeRegExp).join("|")})\\b`,
    "i",
  )

  const manualCustomCount = questionnaire.sections
    .flatMap((s) => s.responsePairs)
    .filter((rp) => {
      const type = (rp.type ?? "free-text") as "free-text" | "boolean" | string
      const isManual = rp.creationSource === "manual"
      const matchesLabel = !!rp.question && labelRegex.test(rp.question)

      return type === targetType && (isManual || matchesLabel)
    }).length

  return `${prefix} ${manualCustomCount + 1}`
}

const PdfAnswerPageUI = ({
  questionnaire,
  isQuestionnairePending,
  fileUrl,
  questionnaireId,
  isContinueLaterModalOpen,
  setIsContinueLaterModalOpen,
  isDeleteModalOpen,
  setIsDeleteModalOpen,
  handleOpenContinueLaterModal,
  handleComplete,
  isUpdateStatusPending,
  addManualField,
  isAddManualFieldPending,
  deleteResponsePair,
  selectedDocumentsCount,
  onSelectDocuments,
  answeringContextEnabled,
  showAnsweringContextButton,
  answeringContextSaved,
  isAnsweringContextOpen,
  setIsAnsweringContextOpen,
  onRegenerateAnswers,
}: {
  questionnaire: Questionnaire
  isQuestionnairePending: boolean
  fileUrl?: string
  questionnaireId: string
  isContinueLaterModalOpen: boolean
  setIsContinueLaterModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  isDeleteModalOpen: boolean
  setIsDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  handleOpenContinueLaterModal: () => void
  handleComplete: (flushPendingEdits: () => Promise<void>) => void
  isUpdateStatusPending: boolean
  addManualField: ReturnType<typeof useAddManualField>["mutateAsync"]
  isAddManualFieldPending: boolean
  deleteResponsePair: ReturnType<typeof useDeleteResponsePair>["mutate"]
  selectedDocumentsCount: number
  onSelectDocuments: () => void
  answeringContextEnabled: boolean
  showAnsweringContextButton: boolean
  answeringContextSaved: boolean
  isAnsweringContextOpen: boolean
  setIsAnsweringContextOpen: React.Dispatch<React.SetStateAction<boolean>>
  onRegenerateAnswers: (flushPendingEdits: () => Promise<void>) => void
}) => {
  const { translate } = useTranslation()
  const [questionnaireData, setQuestionnaireData] = useState(
    () => questionnaire,
  )
  const [activeAddBoxKind, setActiveAddBoxKind] = useState<
    "answer" | "checkbox" | null
  >(null)
  const { breadcrumbs } = useBreadcrumbs()

  const questionnaireCounts = useMemo(
    () => getPdfAnswerCounts(questionnaireData),
    [questionnaireData],
  )

  const updateResponsePairById = useCallback(
    (
      responsePairId: string,
      updater: (
        question: Questionnaire["sections"][number]["responsePairs"][number],
      ) => Questionnaire["sections"][number]["responsePairs"][number],
    ) => {
      setQuestionnaireData((prevData) => {
        if (!prevData) return prevData
        return {
          ...prevData,
          sections: prevData.sections.map((section) => ({
            ...section,
            responsePairs: section.responsePairs.map((question) => {
              if (question.id !== responsePairId) return question
              return updater(question)
            }),
          })),
        }
      })
    },
    [],
  )

  // Preview rendering follows the questionnaire's stored extraction stamp, not the viewer's
  // USE_EXTEND flag, so the overlay matches the downloaded PDF for any viewer.
  const useExtend = questionnaireData?.usesExtend ?? false
  const highlightBoxesArray = useHighlightBoxes(questionnaireData)

  const {
    selectedQuestionId,
    selectedBoxId,
    handleBoxSelect,
    handleInputFocus,
    selectAndScroll,
    clearSelection,
  } = useQuestionSelection()

  const {
    handleDragEnd,
    handleResize,
    updatePlacementForAnswer,
    debouncedUpdatePlacement,
    flush: flushPendingPlacementEdits,
  } = usePlacementUpdater(questionnaire.id, updateResponsePairById)

  const handleSignatureSaveError = useCallback(
    (error: unknown) => {
      toastApiError(error, translate, "ERROR/SOMETHING_WENT_WRONG")
    },
    [translate],
  )

  const signaturePlacements = useSignaturePlacements(
    questionnaire.id,
    questionnaireData.signaturePlacements ?? [],
    handleSignatureSaveError,
  )

  const signatureImageUrls = useSignatureImageUrls(
    questionnaire.id,
    signaturePlacements.placements,
  )

  const signatureHighlightBoxes = useMemo<HighlightBoxType[]>(
    () =>
      signaturePlacements.placements.map((placement) => ({
        id: createSignatureBoxId(placement.id),
        pageNumber: placement.page,
        x: placement.x,
        y: placement.y,
        width: placement.width,
        height: placement.height,
        title: "",
        kind: "signature",
        imageUrl: signatureImageUrls[placement.signatureId],
      })),
    [signaturePlacements.placements, signatureImageUrls],
  )

  const combinedHighlightBoxes = useMemo(
    () => [...highlightBoxesArray, ...signatureHighlightBoxes],
    [highlightBoxesArray, signatureHighlightBoxes],
  )

  const handleAddSignature = useCallback(
    (placement: {
      page: number
      x: number
      y: number
      width: number
      height: number
      signatureId: string
    }) => {
      const placementId = crypto.randomUUID()
      signaturePlacements.addPlacement({
        id: placementId,
        signatureId: placement.signatureId,
        page: placement.page,
        x: placement.x,
        y: placement.y,
        width: placement.width,
        height: placement.height,
      })
      const boxId = createSignatureBoxId(placementId)
      selectAndScroll(boxId, boxId)
    },
    [signaturePlacements, selectAndScroll],
  )

  const handleRemoveSignature = useCallback(
    (boxId: string) => {
      signaturePlacements.removePlacement(
        getSignaturePlacementIdFromBoxId(boxId),
      )
      if (selectedBoxId === boxId) {
        clearSelection()
      }
    },
    [signaturePlacements, selectedBoxId, clearSelection],
  )

  const handleHighlightDragEnd = useCallback(
    (id: string, x: number, y: number) => {
      if (isSignatureBoxId(id)) {
        signaturePlacements.movePlacement(
          getSignaturePlacementIdFromBoxId(id),
          { x, y },
          true,
        )
        return
      }
      handleDragEnd(id, x, y)
    },
    [handleDragEnd, signaturePlacements],
  )

  const handleHighlightResize = useCallback(
    (id: string, layout: LayoutType) => {
      if (isSignatureBoxId(id)) {
        signaturePlacements.movePlacement(
          getSignaturePlacementIdFromBoxId(id),
          layout,
          true,
        )
        return
      }
      handleResize(id, layout)
    },
    [handleResize, signaturePlacements],
  )

  // Backspace/Delete removes the selected signature — gated to signature
  // boxes only, and skipped while focus is in a text input, so it never
  // interferes with normal editing elsewhere on the page.
  useEffect(() => {
    if (!selectedBoxId || !isSignatureBoxId(selectedBoxId)) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Backspace" && event.key !== "Delete") return

      const target = event.target as HTMLElement | null
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return
      }

      event.preventDefault()
      handleRemoveSignature(selectedBoxId)
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [selectedBoxId, handleRemoveSignature])

  const { handleTextChange, handleOptionChange } = useQuestionnaireHandlers(
    questionnaireData,
    questionnaire.id,
    updateResponsePairById,
    updatePlacementForAnswer,
    debouncedUpdatePlacement,
    handleInputFocus,
  )

  // An answer applied from an agent proposal card is already saved server-side,
  // but this component renders a local snapshot of the questionnaire (see
  // questionnaireData) — so refetching upstream would never reach the screen.
  // Fold it into that snapshot through the same path a manual edit takes, which
  // also re-flows the answer's box in the PDF overlay.
  const { registerAnswerAcceptedListener } = useAgentChatContext()
  useEffect(() => {
    registerAnswerAcceptedListener(
      ({
        questionnaireId: acceptedQuestionnaireId,
        responsePairId,
        response,
        citations,
      }) => {
        if (acceptedQuestionnaireId !== questionnaire.id) return
        handleTextChange(responsePairId, response)
        // handleTextChange only touches the response text; the citation badge
        // ("Used N sources") would otherwise keep showing whatever this answer
        // had before, one step behind the text that just changed.
        updateResponsePairById(responsePairId, (q) => ({
          ...q,
          citations: mapApiCitationsToCitations(citations),
        }))
      },
    )
  }, [
    registerAnswerAcceptedListener,
    questionnaire.id,
    handleTextChange,
    updateResponsePairById,
  ])

  const adjustSelectedFontSize = useCallback(
    (delta: number) => {
      if (!selectedBoxId || !selectedQuestionId) return

      updateResponsePairById(selectedQuestionId, (question) => {
        const currentSize =
          question.pdfPlacement?.userPlacement?.fontSize ??
          (question?.pdfPlacement?.extraction?.fontSize ||
            PDF_ANSWER_BOX_DEFAULT_FONT_SIZE)
        const nextSize = clamp(currentSize + delta, 6, 40)

        return updatePlacementForAnswer(question, { fontSize: nextSize }, false)
      })
    },
    [
      selectedBoxId,
      selectedQuestionId,
      updatePlacementForAnswer,
      updateResponsePairById,
    ],
  )

  const handleIncreaseFontSize = useCallback(
    () => adjustSelectedFontSize(1),
    [adjustSelectedFontSize],
  )

  const handleDecreaseFontSize = useCallback(
    () => adjustSelectedFontSize(-1),
    [adjustSelectedFontSize],
  )

  const numTotalQuestions = questionnaireCounts.totalQuestions
  const answeredQuestionCount = questionnaireCounts.answeredTotal
  const hasGeneratedAnswers = questionnaireData.sections.some((section) =>
    section.responsePairs.some((pair) => !!pair.response),
  )

  const handleAddAnswerBox = useCallback(
    async (placement: {
      page: number
      x: number
      y: number
      width: number
      height: number
      font_size: number
    }) => {
      if (isAddManualFieldPending) return

      setActiveAddBoxKind("answer")
      try {
        await loadLabelCatalogs()
        const label = getNextCustomBoxLabel(
          translate,
          questionnaireData,
          "answer",
        )

        const created = await addManualField({
          questionnaireId,
          body: {
            question: label,
            type: "free-text",
            page: placement.page,
            x: placement.x,
            y: placement.y,
            width: placement.width,
            height: placement.height,
            font_size: placement.font_size,
          },
        })

        setQuestionnaireData((prev) => {
          if (!prev) return prev
          const sections = prev.sections ?? []

          if (!sections.length) {
            return {
              ...prev,
              sections: [
                {
                  id: "manual",
                  name: "Manual",
                  organization: prev.organization,
                  questionnaire: prev.id,
                  responsePairs: [
                    {
                      ...created,
                      question: label,
                      type: "free-text",
                      creationSource: "manual",
                      section: "manual",
                    },
                  ],
                },
              ],
            }
          }

          const lastIndex = sections.length - 1
          const targetSection = sections[lastIndex]
          const updatedPair: QuestionnaireResponsePair = {
            ...created,
            question: label,
            type: "free-text",
            creationSource: "manual",
            section: targetSection.id,
          }

          return {
            ...prev,
            sections: sections.map((s, idx) =>
              idx === lastIndex
                ? { ...s, responsePairs: [...s.responsePairs, updatedPair] }
                : s,
            ),
          }
        })

        selectAndScroll(created.id, created.id)
      } finally {
        setActiveAddBoxKind(null)
      }
    },
    [
      addManualField,
      isAddManualFieldPending,
      questionnaireData,
      questionnaireId,
      selectAndScroll,
    ],
  )

  const handleAddCheckboxBox = useCallback(
    async (placement: {
      page: number
      x: number
      y: number
      width: number
      height: number
      font_size: number
    }) => {
      if (isAddManualFieldPending) return

      setActiveAddBoxKind("checkbox")
      try {
        await loadLabelCatalogs()
        const label = getNextCustomBoxLabel(
          translate,
          questionnaireData,
          "checkbox",
        )

        const checkboxOption = {
          text: "x",
          page: placement.page,
          x: placement.x,
          y: placement.y,
          width: placement.width,
          height: placement.height,
        }

        const created = await addManualField({
          questionnaireId,
          body: {
            question: label,
            type: "boolean",
            page: placement.page,
            x: placement.x,
            y: placement.y,
            width: placement.width,
            height: placement.height,
            font_size: placement.font_size,
            options_placements: [checkboxOption],
          },
        })

        setQuestionnaireData((prev) => {
          if (!prev) return prev
          const sections = prev.sections ?? []

          const updatedPair: QuestionnaireResponsePair = {
            ...created,
            question: label,
            type: "boolean",
            response: "x",
            creationSource: "manual",
            section: sections.length
              ? sections[sections.length - 1].id
              : "manual",
            pdfPlacement: {
              ...created.pdfPlacement,
              userPlacement: {
                page: placement.page,
                x: placement.x,
                y: placement.y,
                width: placement.width,
                height: placement.height,
                fontSize: placement.font_size,
                text: "x",
                isUserPositioned: true,
                updatedAt: new Date().toISOString(),
                optionsPlacements: [checkboxOption],
              },
            },
          }

          if (!sections.length) {
            return {
              ...prev,
              sections: [
                {
                  id: "manual",
                  name: "Manual",
                  organization: prev.organization,
                  questionnaire: prev.id,
                  responsePairs: [updatedPair],
                },
              ],
            }
          }

          const lastIndex = sections.length - 1
          return {
            ...prev,
            sections: sections.map((s, idx) =>
              idx === lastIndex
                ? { ...s, responsePairs: [...s.responsePairs, updatedPair] }
                : s,
            ),
          }
        })

        selectAndScroll(created.id, createOptionBoxId(created.id, 0))
      } finally {
        setActiveAddBoxKind(null)
      }
    },
    [
      addManualField,
      isAddManualFieldPending,
      questionnaireData,
      questionnaireId,
      selectAndScroll,
    ],
  )

  const handleDeleteResponsePair = useCallback(
    (responsePairId: string) => {
      setQuestionnaireData((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          sections: prev.sections.map((section) => ({
            ...section,
            responsePairs: section.responsePairs.filter(
              (rp) => rp.id !== responsePairId,
            ),
          })),
        }
      })

      if (
        selectedQuestionId === responsePairId ||
        (selectedBoxId && selectedBoxId.startsWith(`${responsePairId},`))
      ) {
        clearSelection()
      }

      deleteResponsePair({ questionnaireId, responsePairId })
    },
    [
      clearSelection,
      deleteResponsePair,
      questionnaireId,
      selectedBoxId,
      selectedQuestionId,
    ],
  )

  return (
    <Layout
      defaultSidebarState={"closed"}
      headerLeftElement={<BreadcrumbsNavigation breadcrumbs={breadcrumbs} />}
      headerRightElement={
        <div className="flex items-center gap-S">
          {selectedDocumentsCount > 0 ? (
            <div className="flex justify-start">
              <ShadCnButton
                variant="link"
                className="h-auto px-0 text-center"
                onClick={() => onSelectDocuments()}
              >
                {translate("QUESTIONNAIRE_PREVIEW/DOCUMENTS_USED_BUTTON", {
                  count: selectedDocumentsCount,
                })}
              </ShadCnButton>
            </div>
          ) : null}

          <Button
            variantColor="dark"
            title={translate("QUESTIONNAIRE/CONTINUE_LATER")}
            label={translate("QUESTIONNAIRE/CONTINUE_LATER")}
            onClick={handleOpenContinueLaterModal}
            variantSize="small"
          />
          {showAnsweringContextButton ? (
            <div className="relative">
              <Button
                variantColor="dark"
                title={translate("ANSWERING_CONTEXT/OPEN_BUTTON")}
                label={translate("ANSWERING_CONTEXT/OPEN_BUTTON")}
                onClick={() => setIsAnsweringContextOpen(true)}
                variantSize="small"
                IconLeft={IconNotes}
              />
              {answeringContextSaved ? (
                <span
                  role="img"
                  aria-label={translate("ANSWERING_CONTEXT/SAVED_BADGE")}
                  title={translate("ANSWERING_CONTEXT/SAVED_BADGE")}
                  className="absolute -top-1 -right-1 flex size-3.5 items-center justify-center rounded-full bg-success text-black"
                >
                  <IconCheck size={10} aria-hidden="true" />
                </span>
              ) : null}
            </div>
          ) : null}
          <Button
            variantColor="yellow"
            title={translate("QUESTIONNAIRE_ANSWERS/DONE")}
            label={translate("QUESTIONNAIRE_ANSWERS/DONE")}
            onClick={() => {
              handleComplete(async () => {
                await flushPendingPlacementEdits()
                await signaturePlacements.flush()
              })
            }}
            variantSize="small"
            IconRight={IconCheck}
            isDisabled={isUpdateStatusPending}
          />
        </div>
      }
    >
      {/* Modals */}
      {isContinueLaterModalOpen && (
        <ContinueLaterModal
          setIsContinueLaterModalOpen={setIsContinueLaterModalOpen}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteQuestionnaireModal
          questionnaireId={questionnaireId}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
        />
      )}

      {answeringContextEnabled ? (
        <AnsweringContextDialog
          questionnaireId={questionnaireId}
          open={isAnsweringContextOpen}
          onOpenChange={setIsAnsweringContextOpen}
          hasAnswers={hasGeneratedAnswers}
          onRegenerate={() => {
            onRegenerateAnswers(async () => {
              await flushPendingPlacementEdits()
              await signaturePlacements.flush()
            })
          }}
        />
      ) : null}

      <SplitView>
        <SplitViewSection className="bg-background">
          <PdfIntelligenceViewer
            fileUrl={fileUrl || ""}
            highlightBoxesArray={combinedHighlightBoxes}
            onBoxClick={handleBoxSelect}
            selectedBoxId={selectedBoxId}
            onHighlightDragEnd={handleHighlightDragEnd}
            handleIncreaseFontSize={handleIncreaseFontSize}
            handleDecreaseFontSize={handleDecreaseFontSize}
            onHighlightResize={handleHighlightResize}
            onAddAnswerBox={handleAddAnswerBox}
            isAddAnswerBoxPending={
              isAddManualFieldPending && activeAddBoxKind === "answer"
            }
            onAddCheckboxBox={handleAddCheckboxBox}
            isAddCheckboxBoxPending={
              isAddManualFieldPending && activeAddBoxKind === "checkbox"
            }
            onAddSignature={handleAddSignature}
            isAddSignaturePending={signaturePlacements.isSaving}
            onRemoveSignature={handleRemoveSignature}
            fitHighlightBoxText={useExtend}
            useExtend={useExtend}
          />
        </SplitViewSection>

        <SplitViewSection className="bg-dark1 px-M">
          <PdfQASection
            selectedQuestionId={selectedQuestionId}
            questionnaire={questionnaireData}
            isLoading={isQuestionnairePending}
            answeredQuestionCount={answeredQuestionCount}
            numTotalQuestions={numTotalQuestions}
            answeredCountBySection={questionnaireCounts.sectionAnswered}
            totalCountBySection={questionnaireCounts.sectionTotals}
            handleTextChange={handleTextChange}
            handleMultipleChoiceChange={handleOptionChange}
            handleMultipleSelectChange={handleOptionChange}
            onInputFocus={handleInputFocus}
            onDeleteResponsePair={handleDeleteResponsePair}
          />
        </SplitViewSection>
      </SplitView>
    </Layout>
  )
}

export const PdfAnswersPage = () => {
  const { translate } = useTranslation()
  const taggingFlowEnabled = useFeatureFlag("KB_TAGGING_FLOW")
  const answeringContextEnabled = useFeatureFlag("ANSWERING_CONTEXT")
  const navigate = useNavigate()
  const { questionnaireId } = useParams<Record<string, string | undefined>>()
  // Modal states
  const [isContinueLaterModalOpen, setIsContinueLaterModalOpen] =
    useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isSelectDocumentsOpen, setIsSelectDocumentsOpen] = useState(false)
  const [isAnsweringContextOpen, setIsAnsweringContextOpen] = useState(false)

  // Questionnaire data fetching
  const {
    data: questionnaire,
    refetch: refetchQuestionnaire,
    isPending: isQuestionnairePending,
    error: questionnaireError,
  } = useGetQuestionnaireData(questionnaireId, { staleTime: 0, gcTime: 0 })

  // Answering context only applies to v2 (ingested) questionnaires; a legacy
  // questionnaire's saved context would never reach the answering pipeline.
  // The answering-context endpoints require QUEST_RUN; viewers without it get neither the
  // button nor the query behind it.
  const { canRun: canRunQuestionnaire } = usePermissionGuard(
    PERMISSIONS.QUEST_RUN,
  )
  const showAnsweringContextButton =
    answeringContextEnabled &&
    canRunQuestionnaire &&
    questionnaire?.ingestionStatus != null

  const { data: answeringContext } = useGetQuestionnaireAnsweringContext(
    questionnaireId,
    showAnsweringContextButton,
  )
  const answeringContextSaved = !!(
    answeringContext?.text || answeringContext?.terms.length
  )

  const { runAutofillWithRedirect, isPending: isAutofillPending } =
    useQuestionnaireAutofill({
      questionnaireId,
      questionnaire,
      navigate,
      refetchQuestionnaire,
    })
  // Guards against a second regenerate firing while the first is still in
  // flight (e.g. two save-success toasts both invoking the regenerate action).
  // A ref, not state, so the check is synchronous on the very first call.
  const isRegeneratingRef = useRef(false)

  // Status update mutation
  const {
    mutateAsync: updateQuestionnaireStatus,
    isPending: isUpdateStatusPending,
  } = useUpdateQuestionnaireStatus()

  const { mutateAsync: addManualField, isPending: isAddManualFieldPending } =
    useAddManualField()

  const { mutate: deleteResponsePair } = useDeleteResponsePair()

  // What depends on the questionnaire waits for its detail. When the detail fails or is missing these
  // queries stay disabled, and a disabled query with no data stays pending, so the loading branch
  // below must not wait for them.
  const questionnaireAvailable =
    !!questionnaire && !isQuestionnaireNotFound(questionnaireError)

  const { data: fileUrl, isPending: isOriginalFilePending } =
    useGetOriginalFile(questionnaireId, questionnaireAvailable)

  const { data: questionnaireFilterConfig } = useGetQuestionnaireFilterConfig(
    questionnaireId,
    taggingFlowEnabled && questionnaireAvailable,
  )

  const selectedDocumentsCount = taggingFlowEnabled
    ? (questionnaireFilterConfig?.matchedCount ?? 0)
    : 0
  const selectedCategoryFilters = taggingFlowEnabled
    ? (questionnaireFilterConfig?.categoryFilters ?? [])
    : []
  const selectedTagIds = selectedCategoryFilters.flatMap(
    (categoryFilter) => categoryFilter.tagIds,
  )
  const excludeExpired = taggingFlowEnabled
    ? (questionnaireFilterConfig?.excludeExpired ?? false)
    : false

  const [, setQuestionnaireStatus] =
    useQuestionnaireStatusNavigation(questionnaire)

  // Let the floating agent chat know what the user is looking at.
  const { pathname } = useLocation()
  const { targetedAnswer, clearTargetedAnswer } = useAgentChatContext()
  const allVisibleResponsePairIds = (questionnaire?.sections ?? []).flatMap(
    (section) => section.responsePairs.map((pair) => pair.id),
  )
  useRegisterAgentContext({
    route: pathname,
    entities: {
      questionnaire_id: questionnaireId ?? "",
      visible_response_pair_ids:
        targetedAnswer && targetedAnswer.questionnaireId === questionnaireId
          ? [targetedAnswer.responsePairId]
          : allVisibleResponsePairIds,
    },
  })
  // A target only makes sense while its questionnaire is on screen — leaving
  // the page without clearing it would silently narrow agent context on
  // whatever page the user visits next. Reads latest values via ref so the
  // cleanup only fires on true unmount, not on every retarget within the page.
  const unmountClearRef = useRef({
    questionnaireId,
    targetedAnswer,
    clearTargetedAnswer,
  })
  unmountClearRef.current = {
    questionnaireId,
    targetedAnswer,
    clearTargetedAnswer,
  }
  useEffect(() => {
    return () => {
      const current = unmountClearRef.current
      if (current.targetedAnswer?.questionnaireId === current.questionnaireId) {
        current.clearTargetedAnswer()
      }
    }
  }, [])
  // Total questions is derived from questionnaire data.

  // Event handlers
  const handleOpenContinueLaterModal = () => {
    setIsContinueLaterModalOpen(true)
  }

  // Any manual edit still sitting in the PDF page's placement debounce (answer
  // box drag/resize, or a signature add/move) must be flushed and persisted
  // before marking the questionnaire complete, or it's silently lost — same
  // race handleRegenerateAnswers already guards against.
  const handleComplete = async (flushPendingEdits: () => Promise<void>) => {
    try {
      await flushPendingEdits()
      const updateStatusResponse = await updateQuestionnaireStatus({
        questionnaireId,
        status: QuestionnaireStatus.Completed,
      })

      await refetchQuestionnaire()
      setQuestionnaireStatus(updateStatusResponse.status)
    } catch (error) {
      // The axios interceptor already showed the limit toast with its upgrade action
      if (!isLimitError(error)) {
        toast.error(translate("QUESTIONNAIRE_ANSWERS/STATUS_UPDATE_ERROR"))
      }
    }
  }

  // Regenerating from the answering-context dialog reuses the same auto-fill
  // trigger as the Questions page; it redirects to the generating-answers
  // route and back, which refetches this questionnaire on remount. Any manual
  // edit still sitting in the PDF page's placement debounce must be flushed
  // and persisted first, or the redirect races it and autofill overwrites it.
  const handleRegenerateAnswers = async (
    flushPendingEdits: () => Promise<void>,
  ) => {
    if (!questionnaire || !questionnaireId) {
      toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_AUTOFILL_ERROR"))
      return
    }

    if (isRegeneratingRef.current || isAutofillPending) {
      return
    }

    isRegeneratingRef.current = true
    try {
      await flushPendingEdits()
      await runAutofillWithRedirect(readCompanyProfilePreference())
    } catch (error) {
      // The axios interceptor already showed the limit toast with its upgrade action
      if (!isLimitError(error)) {
        toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_AUTOFILL_ERROR"))
      }
    } finally {
      isRegeneratingRef.current = false
    }
  }

  if (isQuestionnaireNotFound(questionnaireError))
    return <ErrorPage variant="not-found" withLayout />

  if (
    isQuestionnairePending ||
    (questionnaireAvailable && isOriginalFilePending)
  ) {
    return (
      <Layout defaultSidebarState={"closed"}>
        <SplitView>
          <SplitViewSection>
            <div className="flex h-full w-full items-center justify-center">
              <LoadingSpinner />
            </div>
          </SplitViewSection>
          <SplitViewSection>
            <>
              <Typography as="h2" variant="titleM" className="pt-XS pb-L">
                {translate("QUESTIONNAIRE_ANSWERS/PAGE_TITLE")}
              </Typography>
              <AnimatedSkeletonList />
            </>
          </SplitViewSection>
        </SplitView>
      </Layout>
    )
  }

  if (!questionnaireId || !questionnaire || !fileUrl) {
    return null
  }

  return (
    <>
      {taggingFlowEnabled ? (
        <SelectDocumentsDialog
          questionnaireId={questionnaireId}
          open={isSelectDocumentsOpen}
          onOpenChange={setIsSelectDocumentsOpen}
          initialSelectedTagIds={selectedTagIds}
          initialHideExpired={excludeExpired}
          readOnly
          readOnlyCategoryFilters={selectedCategoryFilters}
        />
      ) : null}

      <PdfAnswerPageUI
        questionnaireId={questionnaireId}
        questionnaire={questionnaire}
        isQuestionnairePending={isQuestionnairePending}
        fileUrl={fileUrl}
        isContinueLaterModalOpen={isContinueLaterModalOpen}
        setIsContinueLaterModalOpen={setIsContinueLaterModalOpen}
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        handleOpenContinueLaterModal={handleOpenContinueLaterModal}
        handleComplete={handleComplete}
        isUpdateStatusPending={isUpdateStatusPending}
        addManualField={addManualField}
        isAddManualFieldPending={isAddManualFieldPending}
        deleteResponsePair={deleteResponsePair}
        selectedDocumentsCount={selectedDocumentsCount}
        onSelectDocuments={() => setIsSelectDocumentsOpen(true)}
        answeringContextEnabled={answeringContextEnabled}
        showAnsweringContextButton={showAnsweringContextButton}
        answeringContextSaved={answeringContextSaved}
        isAnsweringContextOpen={isAnsweringContextOpen}
        setIsAnsweringContextOpen={setIsAnsweringContextOpen}
        onRegenerateAnswers={handleRegenerateAnswers}
      />
    </>
  )
}
